import React, { useState } from 'react';
import styled from 'styled-components';
import { ClipboardList } from 'lucide-react';
import folderDocumentsApi, {
  FolderDocumentRequestDto,
  DocumentFolderDto,
  FolderDocumentRequestStatus,
  getStatusText,
  getStatusColor,
} from '../../services/folderDocumentService';
import { hexToRgba } from '../../utils/colorUtils';
import ConfirmationModal from './ConfirmationModal';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RequestCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
`;

const RequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 12px;
`;

const RequestTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RequestName = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const RequestMeta = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const StatusBadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: var(--border-radius-md);
  font-size: 12px;
  font-weight: 600;
  background: ${props => hexToRgba(props.color, 0.12)};
  color: ${props => props.color};
  border: 1px solid ${props => hexToRgba(props.color, 0.25)};
`;

const RequestFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: var(--background-secondary);
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 12px;
`;

const ProgressInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProgressBar = styled.div`
  width: 120px;
  height: 6px;
  background: var(--border-color);
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: #16a34a;
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconOnlyButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => {
    if (props.variant === 'primary') return 'var(--primary-color)';
    if (props.variant === 'danger') return '#dc2626';
    return 'var(--text-secondary)';
  }};

  &:hover:not(:disabled) {
    color: ${props => {
      if (props.variant === 'primary') return 'var(--primary-hover)';
      if (props.variant === 'danger') return '#b91c1c';
      return 'var(--primary-color)';
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LinkIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 2L11 13"/>
    <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
  </svg>
);

const CancelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);


const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  opacity: 0.3;
  color: var(--text-secondary);
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 16px;
`;


// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface FolderDocumentRequestsListProps {
  requests: FolderDocumentRequestDto[];
  folders: DocumentFolderDto[];
  onRefresh: () => void;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getUploadUrl = (publicId: string): string => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/folder-upload/${publicId}`;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FolderDocumentRequestsList: React.FC<FolderDocumentRequestsListProps> = ({
  requests,
  folders,
  onRefresh,
}) => {
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
    onConfirm: () => {},
  });

  // Alert modal state for success/error messages
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    variant: 'default' | 'danger';
  }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'default',
  });

  const showAlert = (title: string, message: string, variant: 'default' | 'danger' = 'default') => {
    setAlertModal({ isOpen: true, title, message, variant });
  };

  const handleSendRequest = (requestId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'שליחת בקשה',
      message: 'האם לשלוח את הבקשה במייל?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setActionLoading(requestId);
        try {
          await folderDocumentsApi.sendRequest(requestId);
          onRefresh();
        } catch (error) {
          console.error('Error sending request:', error);
          showAlert('שגיאה', 'שגיאה בשליחת הבקשה', 'danger');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleResendRequest = (requestId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'שליחה חוזרת',
      message: 'האם לשלוח שוב את הבקשה במייל?',
      variant: 'default',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setActionLoading(requestId);
        try {
          await folderDocumentsApi.resendRequest(requestId);
          onRefresh();
        } catch (error) {
          console.error('Error resending request:', error);
          showAlert('שגיאה', 'שגיאה בשליחה חוזרת', 'danger');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleCancelRequest = (requestId: number) => {
    setConfirmModal({
      isOpen: true,
      title: 'ביטול בקשה',
      message: 'האם לבטל את הבקשה? פעולה זו אינה ניתנת לביטול.',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setActionLoading(requestId);
        try {
          await folderDocumentsApi.cancelRequest(requestId);
          onRefresh();
        } catch (error) {
          console.error('Error canceling request:', error);
          showAlert('שגיאה', 'שגיאה בביטול הבקשה', 'danger');
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleCopyLink = async (publicId: string) => {
    const url = getUploadUrl(publicId);
    try {
      await navigator.clipboard.writeText(url);
      showAlert('הועתק', 'הקישור הועתק ללוח');
    } catch (error) {
      showAlert('שגיאה', 'שגיאה בהעתקת הקישור', 'danger');
    }
  };

  if (requests.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>
          <ClipboardList size={48} strokeWidth={1.5} />
        </EmptyIcon>
        <EmptyText>אין בקשות מסמכים</EmptyText>
      </EmptyState>
    );
  }

  return (
    <Container>
      {requests.map(request => {
        const progressPercent = request.totalCount > 0
          ? Math.round((request.receivedCount / request.totalCount) * 100)
          : 0;
        const isLoading = actionLoading === request.id;
        const canSend = request.status === FolderDocumentRequestStatus.Created;
        const canResend = request.status === FolderDocumentRequestStatus.Sent ||
                         request.status === FolderDocumentRequestStatus.PartiallyReceived;
        const canCancel = request.status !== FolderDocumentRequestStatus.Completed &&
                         request.status !== FolderDocumentRequestStatus.Expired;

        return (
          <RequestCard key={request.id}>
            <RequestHeader>
              <RequestTitle>
                <RequestName>{request.requestTitle}</RequestName>
                <RequestMeta>
                  נוצר ב-{formatDate(request.createdAt)}
                  {request.sentAt && ` • נשלח ב-${formatDate(request.sentAt)}`}
                </RequestMeta>
              </RequestTitle>
              <StatusBadge color={getStatusColor(request.status)}>
                {getStatusText(request.status)}
              </StatusBadge>
            </RequestHeader>

            <RequestFooter>
              <ProgressInfo>
                <ProgressBar>
                  <ProgressFill percent={progressPercent} />
                </ProgressBar>
                <ProgressText>
                  {request.receivedCount} מתוך {request.totalCount} התקבלו
                  {request.mandatoryCount > 0 && (
                    <> • {request.mandatoryReceivedCount}/{request.mandatoryCount} חובה</>
                  )}
                </ProgressText>
              </ProgressInfo>

              <ActionButtons>
                {canSend && (
                  <IconOnlyButton
                    variant="primary"
                    onClick={() => handleSendRequest(request.id)}
                    disabled={isLoading}
                    title="שלח במייל"
                  >
                    <SendIcon />
                  </IconOnlyButton>
                )}
                {canResend && (
                  <>
                    <IconOnlyButton onClick={() => handleCopyLink(request.publicId)} title="העתק קישור">
                      <LinkIcon />
                    </IconOnlyButton>
                    <IconOnlyButton
                      variant="primary"
                      onClick={() => handleResendRequest(request.id)}
                      disabled={isLoading}
                      title="שלח שוב"
                    >
                      <SendIcon />
                    </IconOnlyButton>
                  </>
                )}
                {canCancel && (
                  <IconOnlyButton
                    variant="danger"
                    onClick={() => handleCancelRequest(request.id)}
                    disabled={isLoading}
                    title="ביטול"
                  >
                    <CancelIcon />
                  </IconOnlyButton>
                )}
              </ActionButtons>
            </RequestFooter>
          </RequestCard>
        );
      })}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.variant === 'danger' ? 'בטל' : 'שלח'}
      />

      {/* Alert Modal */}
      <ConfirmationModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => setAlertModal(prev => ({ ...prev, isOpen: false }))}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        confirmText="סגור"
        cancelText=""
      />
    </Container>
  );
};

export default FolderDocumentRequestsList;
